import React, { useEffect, useState } from 'react';
import { BookOpen, Upload } from 'lucide-react';
import { adminFetch, AuthError } from './adminApi';
import { inputStyle, labelStyle } from '../quote/quoteStyles';

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

export const ProductsAdmin = ({ onAuthError }) => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', price: '29', file: null });
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    adminFetch('/api/inbox/products').then((r) => r.json()).then(setProducts).catch((e) => { if (e instanceof AuthError) onAuthError(); });
  }, [reloadKey, onAuthError]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.file) return;
    setSaving(true); setMessage('');
    const body = new FormData();
    body.append('title', form.title); body.append('description', form.description); body.append('price_dollars', form.price); body.append('file', form.file);
    try {
      const res = await adminFetch('/api/inbox/products', { method: 'POST', body });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Upload failed');
      setMessage(`"${data.title}" is now for sale at ${money(data.price_cents)}.`);
      setForm({ title: '', description: '', price: '29', file: null });
      load();
    } catch (err) {
      if (err instanceof AuthError) onAuthError(); else setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    await adminFetch(`/api/inbox/products/${p.slug}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !p.active }) });
    load();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 420px)', gap: '2rem', alignItems: 'start' }} className="inbox-layout">
      <div style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', overflowX: 'auto' }}>
        <table data-testid="products-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.92rem' }}>
          <thead><tr style={{ backgroundColor: 'var(--brand-primary)', color: '#fff', textAlign: 'left' }}>{['Product', 'Type', 'Price', 'Status', ''].map((h) => <th key={h} style={{ padding: '0.85rem 1rem', fontWeight: '600' }}>{h}</th>)}</tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.slug} data-testid={`product-row-${p.slug}`} style={{ borderTop: '1px solid var(--border-light)' }}>
                <td style={{ padding: '0.8rem 1rem' }}><strong>{p.title}</strong>{p.filename && <><br /><span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{p.filename}</span></>}</td>
                <td style={{ padding: '0.8rem 1rem' }}>{p.kind === 'access' ? 'Access' : 'eBook'}</td>
                <td style={{ padding: '0.8rem 1rem', fontWeight: '700' }}>{money(p.price_cents)}</td>
                <td style={{ padding: '0.8rem 1rem' }}>{p.active ? 'For sale' : 'Hidden'}</td>
                <td style={{ padding: '0.8rem 1rem' }}><button type="button" data-testid={`product-toggle-${p.slug}`} onClick={() => toggle(p)} className="btn-secondary" style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}>{p.active ? 'Hide' : 'Show'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <form onSubmit={create} data-testid="product-form" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '12px', padding: '1.5rem', display: 'grid', gap: '0.9rem' }}>
        <h3 className="heading-3" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}><BookOpen size={18} color="var(--brand-primary)" /> Add a guide / eBook</h3>
        <div><label htmlFor="product-title" style={labelStyle}>Title</label><input id="product-title" data-testid="product-title" required maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={inputStyle} /></div>
        <div><label htmlFor="product-description" style={labelStyle}>Description</label><textarea id="product-description" data-testid="product-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
        <div><label htmlFor="product-price" style={labelStyle}>Price (USD)</label><input id="product-price" data-testid="product-price" type="number" min="1" step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} style={inputStyle} /></div>
        <div>
          <label htmlFor="product-file" style={labelStyle}>PDF file</label>
          <input id="product-file" data-testid="product-file" type="file" accept=".pdf" required onChange={(e) => setForm({ ...form, file: e.target.files[0] || null })} />
        </div>
        <button type="submit" className="btn-primary" data-testid="product-submit" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}><Upload size={16} /> {saving ? 'Uploading...' : 'Publish for sale'}</button>
        {message && <p data-testid="product-message" style={{ margin: 0, fontSize: '0.85rem', color: message.includes('for sale') ? '#2f855a' : '#c53030' }}>{message}</p>}
      </form>
    </div>
  );
};
